import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  confirmPLCControllerLifecycle,
  confirmPLCControllerDraftChange,
  createPLCController,
  deletePLCController,
  getPLCControllerRuntimeSupport,
  listPLCControllerEvents,
  listPLCControllerRuntimes,
  listPLCControllers,
  listPLCProgramsForFleet,
  previewPLCControllerLifecycle,
  previewPLCControllerDraftChange,
  updatePLCController,
} from "../../../api";
import refreshIcon from "../../../assets/refresh.svg";
import { useNotify } from "../../../ToastContext";
import { usePermission } from "../../../UserContext";
import {
  controllerLifecycleActionAvailability,
  controllerCanEditDraft,
  controllerTargetKey,
  plcControllerLifecycleCommandMessage,
  validPLCControllerLifecycleConfirmation,
  validPLCControllerLifecycleProposal,
} from "./plc-controllers-model";
import {
  ControllerCard,
  ControllerDraftModal,
  ControllerEventsModal,
  ControllerLifecycleModal,
  DeleteControllerModal,
  DraftChangeModal,
  SupportEvidenceModal,
  lifecycleActionLabel,
  loadingOutputDelivery,
  outputDeliveryRefreshMilliseconds,
  readOutputDeliveryBatch,
} from "./plc-controllers.parts";

export function newControllerMutationKey() {
  const secureRandom = window.crypto;
  if (typeof secureRandom?.randomUUID === "function") return secureRandom.randomUUID();
  if (typeof secureRandom?.getRandomValues !== "function") {
    throw new Error("This browser cannot generate a secure controller request identity.");
  }
  const bytes = secureRandom.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
export function PlcControllersPage({ embedded = false }) {
  const notify = useNotify();
  const canConfigure = usePermission("configure");
  const [controllers, setControllers] = useState([]);
  const [runtimes, setRuntimes] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [capability, setCapability] = useState(null);
  const [outputDeliveryByController, setOutputDeliveryByController] = useState({});
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [supportPayload, setSupportPayload] = useState(null);
  const [supportLoading, setSupportLoading] = useState(false);
  const [supportError, setSupportError] = useState("");
  const mountedRef = useRef(true);
  const inventoryGenerationRef = useRef(0);
  const outputRefreshRef = useRef({
    inFlight: false, generation: 0, pending: null, activeRequests: new Set(), cursor: 0,
  });

  useEffect(() => {
    mountedRef.current = true;
    const outputState = outputRefreshRef.current;
    return () => {
      mountedRef.current = false;
      inventoryGenerationRef.current += 1;
      outputState.generation += 1;
      outputState.pending = null;
      outputState.activeRequests.forEach((requestController) => requestController.abort());
      outputState.activeRequests.clear();
    };
  }, []);

  const refreshOutputDelivery = useCallback((nextControllers, { showLoading = false } = {}) => {
    if (!mountedRef.current) return Promise.resolve();
    const state = outputRefreshRef.current;
    // A periodic tick never stacks behind an existing batch. Inventory
    // refreshes do supersede older work so removed controllers cannot return.
    if (state.inFlight && !showLoading) return Promise.resolve();
    const request = {
      controllers: Array.isArray(nextControllers) ? [...nextControllers] : [],
      generation: state.generation + 1,
    };
    state.generation = request.generation;
    if (showLoading) {
      state.cursor = 0;
      state.activeRequests.forEach((requestController) => requestController.abort());
      state.activeRequests.clear();
      setOutputDeliveryByController(Object.fromEntries(
        request.controllers.map((controller) => [controller.id, loadingOutputDelivery])
      ));
    }
    if (state.inFlight) {
      state.pending = request;
      return Promise.resolve();
    }
    const run = async () => {
      state.inFlight = true;
      let current = request;
      try {
        while (current && mountedRef.current) {
          const batch = current;
          const nextCursor = await readOutputDeliveryBatch(
            batch.controllers,
            state.cursor,
            () => mountedRef.current && batch.generation === state.generation,
            state.activeRequests,
            (controllerID, status) => {
              if (!mountedRef.current || batch.generation !== state.generation) return;
              setOutputDeliveryByController((existing) => {
                if (!Object.prototype.hasOwnProperty.call(existing, controllerID)) return existing;
                return { ...existing, [controllerID]: status };
              });
            }
          );
          if (mountedRef.current && batch.generation === state.generation) {
            state.cursor = nextCursor;
          }
          current = state.pending;
          state.pending = null;
        }
      } finally {
        state.inFlight = false;
      }
    };
    return run();
  }, []);

  const load = useCallback(async () => {
    const generation = ++inventoryGenerationRef.current;
    setLoading(true);
    try {
      const [controllerPayload, runtimePayload, programPayload] = await Promise.all([
        listPLCControllers(), listPLCControllerRuntimes(), listPLCProgramsForFleet(),
      ]);
      if (!mountedRef.current || generation !== inventoryGenerationRef.current) return;
      const nextControllers = Array.isArray(controllerPayload?.controllers) ? controllerPayload.controllers : [];
      setControllers(nextControllers);
      setRuntimes(Array.isArray(runtimePayload?.runtimes) ? runtimePayload.runtimes : []);
      setPrograms(Array.isArray(programPayload?.programs) ? programPayload.programs : []);
      setCapability(controllerPayload?.capability || runtimePayload?.capability || null);
      refreshOutputDelivery(nextControllers, { showLoading: true });
    } catch (error) {
      if (!mountedRef.current || generation !== inventoryGenerationRef.current) return;
      notify({ type: "error", title: "Controllers unavailable", message: error.message || "Could not load virtual controller inventory." });
    } finally {
      if (mountedRef.current && generation === inventoryGenerationRef.current) setLoading(false);
    }
  }, [notify, refreshOutputDelivery]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (controllers.length === 0) return undefined;
    const timer = window.setInterval(
      () => refreshOutputDelivery(controllers),
      outputDeliveryRefreshMilliseconds
    );
    return () => window.clearInterval(timer);
  }, [controllers, refreshOutputDelivery]);

  useEffect(() => {
    const openProposal = (event) => {
      const detail = event.detail?.payload || {};
      const controllerName = String(detail.controller_name || detail.proposal?.controller?.name || "");
      const targetName = String(detail.target_name || detail.proposal?.target?.name || "");
      const matches = controllers.filter((item) => (
        item.name === controllerName && (!targetName || item.target?.name === targetName)
      ));
      const controller = matches.length === 1 ? matches[0] : null;
      if (!controller || !controllerCanEditDraft(controller)) {
        notify({ type: "error", title: "Controller proposal unavailable", message: "The stopped controller draft changed or is no longer available. Ask AI to create a fresh proposal." });
        return;
      }
      setModal({ kind: "review", controller, proposal: detail.proposal || null });
    };
    window.addEventListener("gw-ai-propose-plc-controller-draft-change-page", openProposal);
    return () => window.removeEventListener("gw-ai-propose-plc-controller-draft-change-page", openProposal);
  }, [controllers, notify]);

  useEffect(() => {
    const openLifecycleProposal = (event) => {
      const detail = event.detail?.payload || {};
      const controllerName = String(detail.controller_name || "");
      const targetName = String(detail.target_name || "");
      const action = String(detail.action || "");
      const matches = controllers.filter((item) => (
        item.name === controllerName && (!targetName || item.target?.name === targetName)
      ));
      const controller = matches.length === 1 ? matches[0] : null;
      const availability = controllerLifecycleActionAvailability(controller, action, capability, canConfigure);
      if (!controller || !availability.enabled) {
        notify({
          type: "error",
          title: "Lifecycle proposal unavailable",
          message: availability.reason || "The controller changed or is no longer available. Ask AI to create a fresh preview.",
        });
        return;
      }
      if (!validPLCControllerLifecycleProposal(detail.preview, controller, action)) {
        notify({ type: "error", title: "Lifecycle proposal unavailable", message: "The reviewed lifecycle preview was invalid. Create a fresh preview." });
        return;
      }
      setModal({
        kind: "lifecycle",
        controller,
        action,
        reason: String(detail.reason || ""),
        preview: detail.preview,
      });
    };
    window.addEventListener("gw-ai-propose-plc-controller-lifecycle-page", openLifecycleProposal);
    return () => window.removeEventListener("gw-ai-propose-plc-controller-lifecycle-page", openLifecycleProposal);
  }, [controllers, capability, canConfigure, notify]);

  const saveDraft = async (payload) => {
    setBusy(true);
    try {
      if (modal?.kind === "edit") {
        await updatePLCController(modal.controller.id, payload, newControllerMutationKey());
      } else {
        await createPLCController(payload, newControllerMutationKey());
      }
      notify({ type: "success", title: "Controller draft saved", message: "Configuration was stored. No worker was started and plant I/O was not changed." });
      setModal(null);
      await load();
    } catch (error) {
      notify({ type: "error", title: "Controller draft failed", message: error.message || "Could not save the controller draft." });
    } finally {
      setBusy(false);
    }
  };

  const removeDraft = async (reason) => {
    setBusy(true);
    try {
      await deletePLCController(modal.controller.id, { expected_version: modal.controller.version, reason }, newControllerMutationKey());
      notify({ type: "success", title: "Controller draft deleted", message: "The stopped configuration draft was removed. Legacy PLC execution was not changed." });
      setModal(null);
      await load();
    } catch (error) {
      notify({ type: "error", title: "Controller delete failed", message: error.message || "Could not delete the controller draft." });
    } finally {
      setBusy(false);
    }
  };

  const previewDraftChange = async (payload) => {
    setBusy(true);
    try {
      return await previewPLCControllerDraftChange(modal.controller.id, payload, newControllerMutationKey());
    } catch (error) {
      notify({ type: "error", title: "Controller preview failed", message: error.message || "The selected applications and resource limits were not admitted." });
      return null;
    } finally {
      setBusy(false);
    }
  };

  const confirmDraftChange = async (proposal) => {
    setBusy(true);
    try {
      await confirmPLCControllerDraftChange(modal.controller.id, {
        expected_version: proposal.controller.expected_version,
        confirmation_token: proposal.confirmation.token,
        proposal_sha256: proposal.confirmation.proposal_sha256,
      }, newControllerMutationKey());
      notify({
        type: "success",
        title: "Controller draft updated",
        message: "The reviewed assignments and resource policy were saved. Controller execution and plant I/O were not changed.",
      });
      setModal(null);
      await load();
    } catch (error) {
      notify({ type: "error", title: "Controller confirmation failed", message: error.message || "The proposal expired or the controller changed. Preview it again." });
    } finally {
      setBusy(false);
    }
  };

  const previewLifecycle = async (action, reason, retainedDisposition = "", forceDisposition = "") => {
    setBusy(true);
    try {
      const request = {
        action,
        expected_version: modal.controller.version,
        reason,
        retained_disposition: action === "restart" ? retainedDisposition : "",
        force_disposition: action === "restart" ? forceDisposition : "",
      };
      const result = await previewPLCControllerLifecycle(
        modal.controller.id,
        request,
        newControllerMutationKey()
      );
      if (!validPLCControllerLifecycleProposal(result, modal.controller, action)) {
        throw new Error("The gateway returned an invalid lifecycle review. Refresh and try again.");
      }
      return result;
    } catch (error) {
      notify({
        type: "error",
        title: `${lifecycleActionLabel(action)} preview failed`,
        message: error.message || "The controller lifecycle preview failed safely.",
      });
      return null;
    } finally {
      setBusy(false);
    }
  };

  const confirmLifecycle = async (
    action,
    proposal,
    acknowledgeRetainedReset = false,
    acknowledgeForceClear = false
  ) => {
    setBusy(true);
    try {
      const result = await confirmPLCControllerLifecycle(modal.controller.id, {
        confirmation_token: proposal.confirmation_token,
        proposal_sha256: proposal.proposal_sha256,
        acknowledge_retained_reset: acknowledgeRetainedReset,
        acknowledge_force_clear: acknowledgeForceClear,
      }, newControllerMutationKey());
      if (!validPLCControllerLifecycleConfirmation(result, action, proposal)) {
        throw new Error("The gateway returned an invalid lifecycle confirmation. Refresh before taking another action.");
      }
      notify({
        type: "success",
        title: `${lifecycleActionLabel(action)} queued`,
        message: plcControllerLifecycleCommandMessage(result.command.status),
      });
      setModal(null);
      await load();
    } catch (error) {
      notify({
        type: "error",
        title: `${lifecycleActionLabel(action)} confirmation failed`,
        message: error.message || "The controller lifecycle confirmation failed safely.",
      });
    } finally {
      setBusy(false);
    }
  };

  const openEvents = async (controller) => {
    setEvents([]);
    setEventsLoading(true);
    setModal({ kind: "events", controller });
    try {
      const payload = await listPLCControllerEvents(controller.id, 100);
      setEvents(Array.isArray(payload?.events) ? payload.events : []);
    } catch (error) {
      notify({ type: "error", title: "Controller history unavailable", message: error.message || "Could not load controller history." });
    } finally {
      setEventsLoading(false);
    }
  };

  const openSupport = async (runtime) => {
    setSupportPayload(null);
    setSupportError("");
    setSupportLoading(true);
    setModal({ kind: "support", runtime });
    try {
      const payload = await getPLCControllerRuntimeSupport(runtime.kind, runtime.id, 20);
      setSupportPayload(payload);
    } catch (error) {
      setSupportError(error.message || "Authenticated host-support evidence could not be read.");
      notify({ type: "error", title: "Host support unavailable", message: error.message || "Could not load authenticated support evidence." });
    } finally {
      setSupportLoading(false);
    }
  };

  return (
    <div className="gwContentGrid">
      <section className="gwFormStack">
        <div className={embedded ? "plcRuntimeFleetSubheader" : "gwPageHeader"}>
          <div><div className={embedded ? "gwSectionTitle" : "gwPageTitle"}>{embedded ? "Controllers" : "Virtual PLC Controllers"}</div><div className="gwPageSubtitle">Organize PLC applications by controller and see desired versus independently reported runtime truth.</div></div>
          <div className="plcControllerHeaderActions">
            <button type="button" className="gwIconButton" onClick={load} disabled={loading} aria-label="Refresh controllers" title="Refresh">
              <img src={refreshIcon} alt="" className="gwIcon" />
            </button>
            {capability?.executable ? <button type="button" className="gwPrimaryBtn" onClick={() => setModal({ kind: "create" })} disabled={!canConfigure || runtimes.length === 0}>New controller draft</button> : null}
          </div>
        </div>

        <div className="gwNotice isSoft plcControllerTruthNotice" data-gw-card="plc_controller_truth">
          <strong>{capability?.executable ? "Dedicated controller execution available" : "Dedicated controller execution is not available yet"}</strong>
          <span>{capability?.message || "Controller inventory is loading."}</span>
          {!capability?.executable && <span>Lifecycle controls stay disabled until isolated controller execution is ready in this build.</span>}
          {capability?.executable && <span>Every available lifecycle action requires an exact preview and explicit human confirmation. Restart appears only when the backend advertises authenticated atomic generation cutover.</span>}
        </div>

        <div className="gwCardSection" data-gw-card="plc_controllers">
          {!embedded && <div className="gwSectionTitleRow gwSectionTitle"><span>Controllers</span><span className="gwSectionBadge">{controllers.length}</span></div>}
          <div className="gwCardSectionBody plcControllerList">
            {loading && <div className="gwHint">Loading controller inventory…</div>}
            {!loading && controllers.length === 0 && <div className="gwEmptyState gwEmptyCompact"><div className="gwEmptyTitle">No controllers inventoried</div><div className="gwEmptyBody">Create a stopped configuration draft. It will not execute until the isolated runtime is independently admitted.</div></div>}
            {controllers.map((controller) => <ControllerCard key={controller.id} controller={controller} outputDelivery={outputDeliveryByController[controller.id]} capability={capability} canConfigure={canConfigure} onEdit={(item) => setModal({ kind: "edit", controller: item })} onReview={(item) => setModal({ kind: "review", controller: item })} onLifecycle={(item, action) => setModal({ kind: "lifecycle", controller: item, action })} onDelete={(item) => setModal({ kind: "delete", controller: item })} onEvents={openEvents} />)}
          </div>
        </div>
      </section>

      <aside className="gwSidePanel">
        <div className="gwSidePanelTitle">Runtime targets</div>
        <div className="gwSidePanelBody plcControllerRuntimeList">
          {runtimes.map((runtime) => (
            <div key={controllerTargetKey(runtime)}>
              <strong>{runtime.name}</strong>
              <span>{runtime.platform}/{runtime.architecture} · {runtime.connectivity}</span>
              <small>{runtime.isolation?.executable ? "Isolation executable" : "Not isolated on this host"}</small>
              <button type="button" className="gwGhostBtn" onClick={() => openSupport(runtime)}>View support evidence</button>
            </div>
          ))}
        </div>
      </aside>

      {(modal?.kind === "create" || modal?.kind === "edit") && <ControllerDraftModal controller={modal.controller} runtimes={runtimes} busy={busy} onClose={() => setModal(null)} onSubmit={saveDraft} />}
      {modal?.kind === "review" && <DraftChangeModal key={modal.proposal?.confirmation?.proposal_sha256 || modal.controller.id} controller={modal.controller} programs={programs} initialProposal={modal.proposal} busy={busy} onClose={() => setModal(null)} onPreview={previewDraftChange} onConfirm={confirmDraftChange} />}
      {modal?.kind === "lifecycle" && <ControllerLifecycleModal key={`${modal.controller.id}-${modal.action}-${modal.preview?.proposal?.proposal_sha256 || "new"}`} controller={modal.controller} action={modal.action} initialReason={modal.reason} initialPreview={modal.preview} busy={busy} onClose={() => setModal(null)} onPreview={previewLifecycle} onConfirm={confirmLifecycle} />}
      {modal?.kind === "delete" && <DeleteControllerModal controller={modal.controller} busy={busy} onClose={() => setModal(null)} onDelete={removeDraft} />}
      {modal?.kind === "events" && <ControllerEventsModal controller={modal.controller} events={events} loading={eventsLoading} onClose={() => setModal(null)} />}
      {modal?.kind === "support" && <SupportEvidenceModal runtime={modal.runtime} payload={supportPayload} loading={supportLoading} error={supportError} onClose={() => setModal(null)} />}
    </div>
  );
}
